import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewChild,
  ViewEncapsulation,
  OnInit,
  ViewChildren,
  QueryList,
  OnDestroy,
  DestroyRef,
  inject,
  signal,
  WritableSignal,
  computed,
} from '@angular/core';
import {
  ActivatedRoute,
  Data,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { MatDrawer } from '@angular/material/sidenav';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import {
  BehaviorSubject,
  EMPTY,
  filter,
  noop,
  Observable,
  Subject,
  catchError,
  debounceTime,
  tap,
  throttleTime,
  withLatestFrom,
} from 'rxjs';
import { Actions, ofActionDispatched, ofActionErrored, ofActionSuccessful, Select, Store } from '@ngxs/store';
import zip from 'lodash-es/zip';
import { Map } from 'mapbox-gl';
import * as chroma from 'chroma-js';
import formatNumber from 'accounting-js/lib/formatNumber.js';
import { PopupComponent } from 'ngx-mapbox-gl/lib/popup/popup.component';
import { UserState } from '@state/user/state';
import { AccountState } from '@state/account/state';
import { AccountService } from 'app/core';
import { Research } from './state/actions';
import { ResearchState } from './state/state';
import { SetUser } from '@state/user/actions';
import { environment } from '@env/environment';
import { RelatedToType } from 'app/shared/enums';
import UrlUtils from 'app/shared/Utils/url.utils';
import { ResearchService } from './research.service';
import { LayoutService } from 'app/layout/layout.service';
import { UserService } from 'app/layout/common/user/user.service';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Account, AccountColor, FieldDef, QueryConfig, User } from 'app/core/models';
import { Pipeline, Project, Property } from './../projects/projects.types';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { floodMapPaintConfig } from './@components/flood-map-legend/flood-map-paint-config';
import { LegendItem } from './@components/deal-status-legend/deal-status-legent.types';
import { floodMapLegendItems } from './@components/flood-map-legend/flood-map-legend-items';
import { SaveParcelModalComponent } from './save-parcel-modal/save-parcel-modal.component';
import { FieldLayoutOptions } from 'app/shared/components/field-layout-edit/field-layout-edit.types';
import { FieldLayoutEditComponent } from 'app/shared/components/field-layout-edit/field-layout-edit.component';
import { Parcel, LatLng, Layer, BoundingBox, FilterForm, ResearchLayoutKeys, MarkerType } from './research.types';
import { ConfirmationDialogService } from '@shared/components/widgets/dialog/confirmation/confirmation-dialog.service';
import { take } from 'rxjs/operators';
import {
  MapMarkerLayerService,
  DealMarkerLayerMetaData,
  PropertyMarkerLayerData,
  MapMarkerLayerData,
} from './services/map-marker-layer.service';

declare type MapBoxStyleInterface = {
  value: string;
  label: string;
};

const streetMapStyleMatcher = 'mapbox/streets-v12' as const;
const satelliteMapStyleMatcher = 'mapbox/satellite-streets-v12' as const;

export type MapStyleMatchers = typeof streetMapStyleMatcher | typeof satelliteMapStyleMatcher;

@Component({
  selector: 'research',
  templateUrl: './research.component.html',
  styleUrls: ['./research.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResearchComponent implements OnInit, OnDestroy {
  parceledTileServerUrl = environment.tilesApi.url;
  map: Map;
  parcels: any[];
  center: LatLng = { latitude: 39.09747, longitude: -94.57989 };
  userHasGeoLocation = false;
  parcelTilesVisible: 'visible' | 'none' = 'visible';
  selectedLayers: Layer[] = [];
  selectedLayerPoint: any;
  showParcelBoundaries = true;
  markersToDisplay: MarkerType = null;
  // geo json layers
  geoData: any;
  geoProperty: string;
  dealGeoData: any;
  geoStops: [] | [unknown, unknown][];
  dealStatusLegend: LegendItem[];
  filteredProperties: WritableSignal<Property[]> = signal([]);
  filteredDeals: WritableSignal<Project[]> = signal([]);

  floodMapOverlayImages = floodMapLegendItems;
  layerFlood = floodMapPaintConfig;
  selectedParcelFeature = null;
  data: any;
  accountColors: AccountColor[] = [];
  drawerMode: 'over' | 'side';
  drawerOpened: boolean;
  account: Account;
  zoom: number = 15;
  mapBoxStyle: string;
  mapBoxStyleSuffix = 'mapbox://styles/';
  mapBoxStyleOptions: Array<MapBoxStyleInterface> = [
    { value: 'mapbox/satellite-streets-v12', label: 'Satellite' },
    { value: 'mapbox/streets-v12', label: 'Street' },
  ];
  defaultFields = {
    project: [['address_string', 'project_stage_id']],
    property: [['address_string', 'property_type_id']],
  };
  isOwnerSearchLoading = false;
  showParcelTable = false;
  lastHoveredRow = null;
  showInfoBox = true;
  loadedParcels$: Observable<Parcel[]>;
  public layerClicked: boolean = false;
  pipelines: Pipeline[];
  readonly viewMode = new BehaviorSubject<MapStyleMatchers>(streetMapStyleMatcher);

  readonly loadingMarkers$ = new BehaviorSubject(false);
  readonly loadingLayerOptions$ = new BehaviorSubject(false);
  readonly boundingBoxChanged$ = new Subject<void>();
  pendingSearch = signal<boolean>(false);

  filteredDealsGeoJSONData = computed(() => {
    return this.markerLayerService.getDealsGeoJSON(
      this.filteredDeals(),
      this.dealsMapLayoutDefinitions,
      this.fieldDefs,
    );
  });
  filteredPropertiesGeoJSONData = computed(() => {
    return this.markerLayerService.getPropertiesGeoJSON(
      this.filteredProperties(),
      this.propertiesMapLayoutDefinitions,
      this.fieldDefs,
    );
  });
  selectedMarkerLayer: WritableSignal<{
    title: string;
    layout: { label: string; value: string }[][];
    x: number;
    y: number;
  }> = signal(null);

  @Select(ResearchState.currentLocation) currentLocation$: Observable<LatLng>;
  @Select(ResearchState.loadedParcels) stateLoadedParcels$: Observable<Parcel[]>;
  @Select(ResearchState.geoData) geoData$: Observable<any>;

  private _queryFilters: Partial<QueryConfig.QueryFilters>;
  private _legendTitle = 'Tract';
  private parcelClickSubject = new Subject<any>();
  private user: User = this.store.selectSnapshot(UserState.getUser);
  private destroyRef = inject(DestroyRef);

  @ViewChild('matDrawer', { static: true }) matDrawer: MatDrawer;
  @ViewChild('matLayerDrawer', { static: true }) matLayerDrawer: MatDrawer;
  @ViewChildren('mapPopups') mapPopups: QueryList<PopupComponent>;

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _activatedRoute: ActivatedRoute,
    private _projectsService: ProjectsService,
    private _propertiesService: PropertiesService,
    private _accountService: AccountService,
    private _userService: UserService,
    private readonly layoutService: LayoutService,
    private readonly router: Router,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly _researchService: ResearchService,
    private _matDialog: MatDialog,
    private _snackBar: MatSnackBar,
    private _router: Router,
    private confirmationDialogService: ConfirmationDialogService,
    private readonly markerLayerService: MapMarkerLayerService,
  ) {
    this.drawerMode = 'side';
    this.drawerOpened = false;
    this.userHasGeoLocation = window.navigator && window.navigator.geolocation ? true : false;

    this.layoutService.emitCommon(ResearchLayoutKeys.SEARCH_OWNER, null);
    this.layoutService.emitCommon(ResearchLayoutKeys.SEARCH_MAILING_ADDRESS, null);
    this.layoutService.emitCommon(ResearchLayoutKeys.VIEW_AS_TABLE, null);
  }

  ngOnInit(): void {
    setTimeout(() => this.layoutService.emitCommon('showActivitiesPanel', false), 0);

    this.handleRouterEvents();
    this.populateMapBoxStyle();
    this.setupMapDefinition();
    this.handleLocalStorageItems();
    this.getDefaultMapMarkersForPage();

    this.accountColors = this.store.selectSnapshot(AccountState.getAccount)?.colors ?? [];

    if (this._activatedRoute.children?.[0]?.snapshot.params.id) {
      this.drawerOpened = true;
    }

    this._projectsService.pipelines$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((pipelines) => {
      if (pipelines) {
        this.pipelines = pipelines;
        this._changeDetectorRef.markForCheck();
      }
    });

    this.loadedParcels$ = this.stateLoadedParcels$.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((parcels: Parcel[]) => (this.parcels = parcels)),
    );

    this._activatedRoute.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: Data) => (this.data = data));

    this._activatedRoute.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (params['lat'] && params['lng']) {
        this.center = { latitude: params['lat'], longitude: params['lng'] };
        this.zoom = 15;
        this.store.dispatch(new Research.SetCurrentLocation(this.center));
      } else if (params['getLocation']) {
        this.goToUserLocation();
      }
    });

    this.currentLocation$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((location: LatLng) => {
      if (location && !this.isNaN(location.latitude) && !this.isNaN(location.longitude)) {
        this.center = location;
        if (this.map) {
          this.map.jumpTo({ center: [location.longitude, location.latitude], zoom: this.zoom });
          this.store.dispatch(new Research.UpdateBoundingBox(this.getBoundingBox()));
        }
      }
      this._changeDetectorRef.markForCheck();
    });

    this.geoData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((geoData: any) => {
      if (geoData) {
        this.geoData = geoData;
        this._massageGeoJson();
      }
      this._changeDetectorRef.markForCheck();
    });

    this.boundingBoxChanged$
      .pipe(
        throttleTime(500),
        takeUntilDestroyed(this.destroyRef),
        filter(() => !!this.map),
      )
      .subscribe(() => this.store.dispatch(new Research.UpdateBoundingBox(this.getBoundingBox())));

    this.layoutService
      .getCommon(ResearchLayoutKeys.VIEW_AS_TABLE)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((parcels: Parcel[] | null) => parcels?.length > 0),
        tap((parcels) => {
          this.store.dispatch(new Research.SetParcels(parcels));
          this.showParcelTable = true;
          this.matDrawer.close().then(() => window.dispatchEvent(new Event('resize')));
          this.isOwnerSearchLoading = false;
          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);

    this.loadParcelActionListeners();
    this.loadLayerActionListeners();

    this.store.dispatch(new Research.LoadAvailableLayers());

    this.parcelClickSubject
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        // hack: this is used to allow ResearchComponent.layerClicked to be set updated to its final value.
        debounceTime(1000),
        tap((event) => {
          if (this.mapPopups?.find((x) => x.popupInstance?.isOpen())) {
            return;
          }

          if (this.layerClicked) {
            return;
          }

          this._researchService
            .reverseGeocode(null, event.lngLat.lat, event.lngLat.lng)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => {
              if (data && data.address) {
                const dialogRef = this._matDialog.open(SaveParcelModalComponent, {
                  data: { reverseGeoCode: data, pipelines: this.pipelines },
                });

                dialogRef.afterClosed().subscribe((newProject) => {
                  if (newProject?.id) {
                    this._snackBar
                      .open('Deal has been saved.', 'Go to Deal', {
                        duration: 3000,
                      })
                      .onAction()
                      .subscribe(() => {
                        this._router.navigate([
                          '/projects',
                          newProject.pipeline_id,
                          newProject.id,
                          'details',
                          'overview',
                        ]);
                      });
                  }
                });
                this._changeDetectorRef.markForCheck();
              }
            });
        }),
      )
      .subscribe(noop);
  }

  get dealsMapLayoutDefinitions(): FieldDef['name'][][] {
    return this.account?.definitions?.map?.project?.fields;
  }

  get propertiesMapLayoutDefinitions(): FieldDef['name'][][] {
    return this.account?.definitions?.map?.property?.fields;
  }

  get fieldDefs(): FieldDef[] {
    return this.data?.fieldDefs;
  }

  get queryFilters(): Partial<QueryConfig.QueryFilters> {
    return this._queryFilters;
  }

  onBoundingBoxChange(): void {
    if (this.map) {
      this.boundingBoxChanged$.next();
    }
  }

  onToggleParcelBoundaries(showParcelBoundaries): void {
    this.showParcelBoundaries = showParcelBoundaries;
  }

  goToUserLocation(): void {
    if (this.userHasGeoLocation) {
      window.navigator.geolocation.getCurrentPosition((position) => {
        this.center = { latitude: position?.coords?.latitude, longitude: position?.coords?.longitude };
        this.store.dispatch(new Research.SetCurrentLocation(this.center));
      });
    }
  }

  onAddressSelected(event: LatLng): void {
    this.store.dispatch(new Research.LoadParcelsByLatLng(event));
  }

  private onMapStyleChanged(mapStyle: MapStyleMatchers): void {
    this.mapBoxStyle = this.mapBoxStyleSuffix + mapStyle;
    UrlUtils.saveToLocalStorage('mapBoxStyle', this.mapBoxStyle);
  }

  private populateMapBoxStyle() {
    this.mapBoxStyle = UrlUtils.getFromLocalStorage('mapBoxStyle');
    if (this.mapBoxStyle) {
      this.viewMode.next(
        this.mapBoxStyle.includes(satelliteMapStyleMatcher) ? streetMapStyleMatcher : satelliteMapStyleMatcher,
      );
      return this.mapBoxStyle;
    } else this.mapBoxStyle = this.mapBoxStyleSuffix + this.mapBoxStyleOptions[0].value;
  }

  private setupMapDefinition(): void {
    this.account = this._accountService.getCurrentAccount();

    if (!this.account.definitions || !this.account.definitions.map) {
      const definitions = {
        ...(this.account.definitions || {}),
        ...{
          map: {
            project: { fields: this.defaultFields.project },
            property: { fields: this.defaultFields.property },
          },
        },
      };

      this.updateDefinition(definitions);
    } else if (Object.keys(this.account.definitions.map).includes('fields')) {
      const definitions = {
        ...this.account.definitions,
        map: {
          project: { fields: this.account.definitions.map.fields },
          property: { fields: this.defaultFields.property },
        },
      };

      this.updateDefinition(definitions);
    }
  }

  /**
   * Return the distance between two points in miles
   */
  private _distance(lat1, lng1, lat2, lng2, unit = 'M'): any {
    if (lat1 == lat2 && lng1 == lng2) {
      return 0;
    } else {
      let radlat1 = (Math.PI * lat1) / 180;
      let radlat2 = (Math.PI * lat2) / 180;
      let theta = lng1 - lng2;
      let radtheta = (Math.PI * theta) / 180;
      let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
      if (dist > 1) {
        dist = 1;
      }
      dist = Math.acos(dist);
      dist = (dist * 180) / Math.PI;
      dist = dist * 60 * 1.1515;
      if (unit == 'K') {
        dist = dist * 1.609344;
      }
      if (unit == 'N') {
        dist = dist * 0.8684;
      }
      return dist;
    }
  }

  /**
   * Given a set of selected filters and the resulting geojson, we will massage the data into a format that we can display on the map
   */
  private _massageGeoJson(): void {
    let geoJson = this.geoData;
    let heatmapLayers = this.filterLayers(['heatmap']);
    this.geoProperty = heatmapLayers[0]['attribute'];

    let geoJsonData = this._getDataFromGeoJSON(geoJson, this.geoProperty);
    this.geoStops = this._returnStops(geoJsonData);
    this._buildLegend(geoJsonData, this.geoStops);
  }

  private _getDataFromGeoJSON(geoJson, property) {
    let data = [];
    geoJson.features.forEach((feature) => {
      let val = feature.properties[property];
      if (!val) {
        val = 0;
      }
      data.push(val);
    });
    let uniqueData = [...Array.from(new Set(data))];
    uniqueData = uniqueData.sort((a, b) => a - b);
    return uniqueData;
  }

  private _returnStops(data) {
    let colorScale = chroma.scale('YlGnBu').domain(data);
    let scaleVals = data.map((x) => colorScale(x).hex());
    return zip(data, scaleVals);
  }

  /**
   * Given the data and the stops, create a legend to display on the map for the data
   */
  private _buildLegend(data, stops) {
    if (data && data.length > 0) {
      let legend = document.getElementById('legend');
      legend.innerHTML = '';
      legend.style.display = 'block';
      let title = document.createElement('div');
      title.className = 'legend-title';
      legend.appendChild(title);
      title.innerHTML = this._legendTitle;

      let item = document.createElement('div');
      item.className = 'gradient';
      legend.appendChild(item);

      let stopsWidth = 325 / stops.length;
      stops.forEach((stop) => {
        let color = stop[1];
        let key = document.createElement('span');
        key.className = 'grad-step';
        key.style.backgroundColor = color;
        key.style.width = stopsWidth + 'px';

        item.appendChild(key);
      });
      let minValue = document.createElement('div');
      minValue.className = 'domain-min';
      minValue.innerHTML = formatNumber(Math.min(...data) * 100).toString() + '% CAGR';
      let maxValue = document.createElement('div');
      maxValue.className = 'domain-max';
      maxValue.innerHTML = formatNumber(Math.max(...data) * 100).toString() + '% CAGR';
      item.append(minValue);
      item.append(maxValue);
    } else {
      let legend = document.getElementById('legend');
      legend.style.display = 'none';
    }
  }

  private _hideLegend(): void {
    let legend = document.getElementById('legend');
    legend.style.display = 'none';
  }

  private _loadGeoData(): void {
    const loadHeatmap = () => {
      //if we have any heatmap layers, then go ahead and fetch that data.
      let heatmapLayers = this.filterLayers(['heatmap']);
      if (heatmapLayers.length > 0) {
        let center = this.map.getCenter();
        let bounds = this.map.getBounds();
        let ne = bounds.getNorthEast();

        let distance = this._distance(ne.lat, ne.lng, center.lat, center.lng);
        if (distance <= 15) {
          this._legendTitle = 'by Census Tract';
          this.store.dispatch(new Research.LoadGeoData(this.center, 'tract', distance));
        } else if (distance <= 75) {
          //don't call the data if miles > 75 or zoomed in to less than 1 mile
          this._legendTitle = 'by Zip Code';
          this.store.dispatch(new Research.LoadGeoData(this.center, 'zcta', distance));
        }
      } else {
        this.geoData = null;
        this._hideLegend();
      }
    };

    if (this.map) {
      loadHeatmap();
    } else {
      let tries = 0;
      const waitMap = (readyFn: () => void, tries: number, max = 5) =>
        setTimeout(() => {
          if (tries < max && !this.map) {
            tries++;
            waitMap(readyFn, tries);
          } else {
            readyFn();
          }
        }, 800);

      waitMap(() => {
        loadHeatmap();
      }, tries);
    }
  }

  onZoomEnd(): void {
    if (this.map) {
      let zoom = this.map.getZoom();
      const currentParcelVisibility = this.parcelTilesVisible;
      if (zoom >= 14) {
        if (currentParcelVisibility == 'none') {
          this.parcelTilesVisible = 'visible';

          this._changeDetectorRef.markForCheck();
        }
      } else {
        if (currentParcelVisibility == 'visible') {
          this.parcelTilesVisible = 'none';
          this._changeDetectorRef.markForCheck();
        }
      }
      this._loadGeoData();
      this.queryObjects(true);
    }
  }

  filterLayers(types: string[]): Layer[] {
    return this.selectedLayers.filter((layer) => types.includes(layer.type));
  }

  hasLayerSelected(type, id): boolean {
    const index = this.selectedLayers.findIndex((layer) => type == layer.type && layer.id == id);
    return index >= 0;
  }

  onParcelClick(event: any): void {
    this.layerClicked = true;
    if (!this.matDrawer.opened) {
      this.matDrawer.open();
    }

    const [feature] = event.features;

    this.store.dispatch(new Research.GetAndSelectParcel(feature.properties.id, 'list'));

    if (!this.router.url.endsWith('research')) {
      this.router.navigate(['research']);
    }
  }

  onSelectLatLng(event: any): void {
    if (event instanceof PointerEvent) {
      return;
    }

    this.layerClicked = false;

    this.parcelClickSubject.next(event);
  }

  onFloodLayerClick($event) {
    this.selectedLayerPoint = {
      properties: $event?.features[0]?.properties,
      lngLat: $event?.lngLat,
    };
  }

  onSelectedLayersChanged(selectedLayersIds: string[]): void {
    this.store.dispatch(new Research.SetSelectedLayersIds(selectedLayersIds));
    this.selectedLayers = this.store.selectSnapshot(ResearchState.selectedLayers);
    UrlUtils.saveToLocalStorage('researchSelectedLayers', this.selectedLayers, true);
  }

  onLayerClick($event, layer): void {
    this.selectedLayerPoint = {
      properties: $event?.features[0]?.properties,
      lngLat: $event?.lngLat,
    };
  }

  onToggleMarkersToDisplay(event: MarkerType): void {
    this.markersToDisplay = event;

    let fjsonValue = '[]';
    if (this.markersToDisplay === RelatedToType.Project) {
      fjsonValue = this.user?.settings?.map_markers_selected?.deals ?? '[]';
    } else if (this.markersToDisplay === RelatedToType.Property) {
      fjsonValue = this.user?.settings?.map_markers_selected?.properties ?? '[]';
    } else {
      fjsonValue = '[]';
    }

    UrlUtils.saveToLocalStorage('researchMarkersToDisplay', this.markersToDisplay, null);

    this.updateMarkersAndFilters({
      fjson: fjsonValue,
    });
  }

  onMarkersFilterChanged(event: Partial<QueryConfig.QueryFilters>): void {
    if (event) {
      this.updateMarkersAndFilters(event);
    }
  }

  private updateMarkersAndFilters(filters: Partial<QueryConfig.QueryFilters>): void {
    this._queryFilters = filters;

    const dealMarkerValue = this.user?.settings?.map_markers_selected?.deals ?? null;
    const propertyMarkerValue = this.user?.settings?.map_markers_selected?.properties ?? null;

    // Update user settings based on marker type
    if (this.markersToDisplay === RelatedToType.Project) {
      this.updateUserSettingsForMapMarkers(this._queryFilters.fjson, propertyMarkerValue);
    } else if (this.markersToDisplay === RelatedToType.Property) {
      this.updateUserSettingsForMapMarkers(dealMarkerValue, this._queryFilters.fjson);
    } else {
      this.updateUserSettingsForMapMarkers(dealMarkerValue, propertyMarkerValue);
    }

    this.queryObjects();
  }

  updateUserSettingsForMapMarkers(dealSettings: string | null, propertySettings: string | null): void {
    this._userService
      .saveSettings({
        map_markers_selected: {
          deals: dealSettings,
          properties: propertySettings,
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user: User) => {
        this.store.dispatch(
          new SetUser({
            ...user,
            roles: this.user.roles,
          }),
        );
        this.user = this.store.selectSnapshot(UserState.getUser);
      });
  }

  getDefaultMapMarkersForPage(): void {
    this.markersToDisplay = UrlUtils.getFromLocalStorage('researchMarkersToDisplay', null);
    if (this.markersToDisplay === RelatedToType.Project) {
      this._queryFilters = { fjson: this.user?.settings?.map_markers_selected?.deals ?? '[]' };
    } else if (this.markersToDisplay === RelatedToType.Property) {
      this._queryFilters = { fjson: this.user?.settings?.map_markers_selected?.properties ?? '[]' };
    } else {
      this._queryFilters = { fjson: '[]' };
      this.markersToDisplay = null;
    }

    this.queryObjects();
  }

  queryObjects(deferSearch = false): void {
    if (deferSearch) {
      this.pendingSearch.set(true);
      return;
    }

    this.pendingSearch.set(false);

    if (this.markersToDisplay === RelatedToType.Project) {
      this.filteredProperties.set([]);
      this.queryProjects();
    } else if (this.markersToDisplay === RelatedToType.Property) {
      this.filteredDeals.set([]);
      this.queryProperties();
    } else {
      this.filteredProperties.set([]);
      this.filteredDeals.set([]);
    }
    this._changeDetectorRef.markForCheck();
  }

  queryProperties(): void {
    if (this.map) {
      const bbox = this.map.getBounds();
      const nw = bbox.getNorthWest();
      const se = bbox.getSouthEast();

      const location = {
        top_left: {
          lat: nw.lat,
          lon: nw.lng,
        },
        bottom_right: {
          lat: se.lat,
          lon: se.lng,
        },
      };

      const filters = { location, ...this._queryFilters };

      this.loadingMarkers$.next(true);

      const fields = [
        ...['id', 'name', 'latitude', 'longitude'],
        ...(this.account.definitions?.map?.property?.fields || this.defaultFields.property).reduce(
          (p, c) => [...p, ...c],
          [],
        ),
      ];

      this._propertiesService
        .getProperties(fields, filters, 0, 100)
        .pipe(
          debounceTime(300),
          takeUntilDestroyed(this.destroyRef),
          catchError(() => {
            this.loadingMarkers$.next(false);
            return EMPTY;
          }),
        )
        .subscribe((response) => {
          if (response && response.properties) {
            this.filteredProperties.set(response.properties);
          }
          this._changeDetectorRef.markForCheck();
          this.loadingMarkers$.next(false);
        });
    } else {
      this.filteredProperties.set([]);
    }
  }

  queryProjects(): void {
    if (this.map) {
      const bbox = this.map.getBounds();
      const nw = bbox.getNorthWest();
      const se = bbox.getSouthEast();

      const location = {
        top_left: {
          lat: nw.lat,
          lon: nw.lng,
        },
        bottom_right: {
          lat: se.lat,
          lon: se.lng,
        },
      };

      const filters = { location, ...this._queryFilters };

      this.loadingMarkers$.next(true);

      const fields = [
        ...['id', 'title', 'latitude', 'longitude', 'pipeline_id', 'address_string', 'properties'],
        ...(this.account.definitions?.map?.project?.fields || this.defaultFields.project).reduce(
          (p, c) => [...p, ...c],
          [],
        ),
      ];

      this._projectsService
        .getProjectsNoPipeline(fields, filters, 0, 1000, 'project_stage_rank', 'desc', filters?.q)
        .pipe(
          debounceTime(300),
          takeUntilDestroyed(this.destroyRef),
          catchError(() => {
            this.loadingMarkers$.next(false);
            return EMPTY;
          }),
        )
        .subscribe((response) => {
          if (response && response.projects) {
            this.filteredDeals.set(response.projects);

            this.dealStatusLegend = this.filteredDeals()
              .reduce((uniqueLegends, { project_stage }) => {
                const existingLegend = uniqueLegends.find((legend) => legend.id === project_stage.id);
                if (!existingLegend) {
                  uniqueLegends.push({
                    id: project_stage.id,
                    color: project_stage.color,
                    label: project_stage.name,
                  });
                }
                return uniqueLegends;
              }, [])
              .sort((a, b) => a.label.localeCompare(b.label));
          }
          this._changeDetectorRef.markForCheck();
          this.loadingMarkers$.next(false);
        });
    } else {
      this.filteredDeals.set([]);
    }
  }

  onMapLoaded(event: { target: Map; type: string }): void {
    this.map = event.target;

    this._changeDetectorRef.markForCheck();

    this.queryObjects();
    this.boundingBoxChanged$.next();
  }

  editLayout(type: RelatedToType.Project | RelatedToType.Property): void {
    this.mapPopups.forEach(({ popupInstance }) => popupInstance.remove());

    this.layoutService.openComponentDrawer<FieldLayoutOptions>(FieldLayoutEditComponent, {
      inputs: {
        ui: { title: 'Edit marker info layout' },
        data: this.data,
        fields: this.getDataForEdit(this.account.definitions.map[type.toLowerCase()]),
        filterFieldsBy: type,
      },
      outputs: {
        onCancel: () => this.layoutService.closeComponentDrawer(),
        onSave: (fields: string[][]) => {
          const definitions = {
            ...this.account.definitions,
            map: {
              ...this.account.definitions.map,
              [type.toLowerCase()]: {
                ...this.account.definitions.map[type.toLowerCase()],
                fields: [...this.transformFields(fields)],
              },
            },
          };

          this.updateDefinition(definitions, () => {
            this.layoutService.closeComponentDrawer();
            this._changeDetectorRef.detectChanges();
          });
        },
      },
    });
  }

  getMarkerFields(type: string): { fields: string[][] } {
    const fields = this.getDataForEdit(this.account.definitions.map[type.toLowerCase()]);
    return fields.length ? { fields } : { fields: this.defaultFields[type.toLowerCase()] };
  }

  onTransformMapRequest(url: string, resourceType): any {
    if (url.includes(environment.tilesApi.url)) {
      return {
        url: url,
        headers: { Auth: `Bearer ${environment.tilesApi.authToken}` },
      };
    }
  }

  onMapMouseOut(): void {
    const canvas = this.map?.getCanvas();
    if (canvas) {
      canvas.style.cursor = '';
      this.selectedParcelFeature = null;
    }
  }

  closeTable(): void {
    this.showParcelTable = false;
    setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
  }

  openResults(): void {
    this.drawerOpened = true;
    this.matDrawer.open();
  }

  runFilter(mapFilter: FilterForm): void {
    if (Object.keys(mapFilter).some((key) => mapFilter[key])) {
      this.store.dispatch(new Research.LoadParcels(mapFilter));
    }
  }

  onClearFilters(): void {
    this.store.dispatch(new Research.ClearFilterAndParcels());
    this.showParcelTable = false;
  }

  toggleDrawer(): void {
    this.matDrawer.toggle().then(() => window.dispatchEvent(new Event('resize')));
  }

  onParcelTableHoverRow(row: any): void {
    this.lastHoveredRow = row;
  }

  toggleMapViewMode(mode: MapStyleMatchers) {
    this.onMapStyleChanged(mode);
    this.viewMode.next(mode === satelliteMapStyleMatcher ? streetMapStyleMatcher : satelliteMapStyleMatcher);
  }

  onLayerMouseMove($event: any) {
    if (this.selectedParcelFeature?.id !== $event.features[0]?.properties?.id) {
      this.map.getCanvas().style.cursor = 'pointer';
      this.selectedParcelFeature = $event.features[0]?.properties;
    }
  }

  onLayerMouseMoveAdditional($event: any) {
    this.map.getCanvas().style.cursor = 'pointer';
    this.selectedParcelFeature = $event.features[0]?.properties;
  }

  onLayerMouseLeave(): void {
    this.map.getCanvas().style.cursor = '';
    this.selectedParcelFeature = null;
  }

  ngOnDestroy(): void {
    this.layoutService.emitCommon('showActivitiesPanel', true);
  }

  private updateDefinition(definitions: any, subscribeFn?: () => void): void {
    this.account.definitions = definitions;

    this._accountService
      .updateAccount({
        id: this.account.id,
        configuration: {
          definitions: JSON.stringify(definitions),
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.account = this._accountService.getCurrentAccount();
        this.queryObjects();
        subscribeFn?.();
      });
  }

  emitSearch(): void {
    this.queryObjects(false);
  }

  onMoveEnd(): void {
    this.queryObjects(true);
  }

  private transformFields(fields: string[][]): string[][] {
    const maxColumnCount = this.getMaxColumnCount(fields);
    const columnsAndRows = [];

    for (let i = 0; i < maxColumnCount; i++) {
      const columnArray = [];
      for (let j = 0; j < fields.length; j++) {
        if (fields[j][i]) {
          columnArray.push(fields[j][i]);
        }
      }
      columnsAndRows.push(columnArray);
    }

    return columnsAndRows;
  }

  private getDataForEdit(definition: { fields: string[][] }): string[][] {
    const maxColumnCount = this.getMaxColumnCount(definition.fields);
    const columnsAndRows = [];

    for (let i = 0; i < maxColumnCount; i++) {
      const columnArray = [];
      for (let j = 0; j < definition.fields.length; j++) {
        if (j !== definition.fields.length - 1 || definition.fields[j][i]) {
          columnArray.push(definition.fields[j][i]);
        }
      }
      columnsAndRows.push(columnArray);
    }

    return columnsAndRows;
  }

  private getMaxColumnCount(fields: string[][]): number {
    return fields.reduce((prev: number, cur: string[]) => {
      return cur.length > prev ? cur.length : prev;
    }, 0);
  }

  private getBoundingBox(): BoundingBox {
    const bbox = this.map.getBounds();
    const nw = bbox.getNorthWest();
    const se = bbox.getSouthEast();

    return {
      ne: [nw.lng, nw.lat],
      sw: [se.lng, se.lat],
    };
  }

  private loadLayerActionListeners(): void {
    this.actions$
      .pipe(ofActionDispatched(Research.LoadAvailableLayers), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadingLayerOptions$.next(true);
      });

    this.actions$
      .pipe(
        ofActionSuccessful(Research.LoadAvailableLayers),
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.loadingLayerOptions$.next(false);
          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(ofActionErrored(Research.LoadAvailableLayers), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadingLayerOptions$.next(false);
        this._changeDetectorRef.detectChanges();
      });
  }

  private loadParcelActionListeners(): void {
    this.actions$.pipe(ofActionDispatched(Research.LoadParcels), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isOwnerSearchLoading = true;
      this.showParcelTable = true;
    });

    this.actions$
      .pipe(
        ofActionSuccessful(Research.LoadParcels),
        withLatestFrom(this.loadedParcels$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.isOwnerSearchLoading = false;
        window.dispatchEvent(new Event('resize'));
        this._changeDetectorRef.detectChanges();
      });

    this.actions$.pipe(ofActionErrored(Research.LoadParcels), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isOwnerSearchLoading = false;
      this.showParcelTable = false;
      this._changeDetectorRef.detectChanges();
    });

    this.actions$
      .pipe(
        ofActionSuccessful(Research.LoadParcelsByLatLng),
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          if (this.store.selectSnapshot(ResearchState.listParcels)?.length > 0) {
            this.openResults();
            this._changeDetectorRef.detectChanges();
          } else {
            this.drawerOpened = false;
            this.matDrawer.close();
          }
        }),
      )
      .subscribe(noop);
  }

  private handleRouterEvents(): void {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          this._changeDetectorRef.detectChanges();
          break;
        }

        case event instanceof NavigationEnd:
        case event instanceof NavigationCancel:
        case event instanceof NavigationError: {
          this._changeDetectorRef.detectChanges();
          break;
        }
        default: {
          break;
        }
      }
    });
  }

  private handleLocalStorageItems(): void {
    this.selectedLayers = UrlUtils.getFromLocalStorage('researchSelectedLayers', true, []);
    this.store.dispatch(new Research.SetSelectedLayersIds(this.selectedLayers.map((layer) => layer.id)));
  }

  isNaN(value: any): boolean {
    return isNaN(value);
  }

  onDealLayerMarkerClick(
    $event: mapboxgl.MapMouseEvent & {
      features?: import('mapbox-gl').MapboxGeoJSONFeature[] | undefined;
    } & mapboxgl.EventData,
  ) {
    this.layerClicked = true;
    const featureData = $event.features[0]?.properties as unknown as DealMarkerLayerMetaData;

    this.confirmationDialogService
      .openConfirmationDialog({
        dialogTitle: featureData.title ?? '-',
        description: 'Do you wish to navigate to this deal?',
        styling: { yes: 'primary' },
        buttonsConfig: { yes: 'Yes', no: 'Cancel' },
      })
      .pipe(
        filter((result) => !!result),
        take(1),
      )
      .subscribe(() => {
        const route = '/projects/' + featureData.pipeline_id + '/' + featureData.project_id + '/details/overview';

        this.router.navigate([route]);
      });
  }

  onPropertyLayerMarkerClick(
    $event: mapboxgl.MapMouseEvent & {
      features?: import('mapbox-gl').MapboxGeoJSONFeature[] | undefined;
    } & mapboxgl.EventData,
  ) {
    this.layerClicked = true;
    const featureData = $event.features[0]?.properties as unknown as PropertyMarkerLayerData;

    this.confirmationDialogService
      .openConfirmationDialog({
        dialogTitle: featureData.title ?? '-',
        description: 'Do you wish to navigate to this property?',
        styling: { yes: 'primary' },
        buttonsConfig: { yes: 'Yes', no: 'Cancel' },
      })
      .pipe(
        filter((result) => !!result),
        take(1),
      )
      .subscribe(() => {
        const route = '/properties/' + featureData.property_id;

        this.router.navigate([route]);
      });
  }

  onMarkerMouseMove($event) {
    this.map.getCanvas().style.cursor = 'pointer';

    const layout: MapMarkerLayerData['layout'] = Object.values(JSON.parse($event.features[0]?.properties?.layout));
    const title: MapMarkerLayerData['title'] = $event.features[0]?.properties?.title ?? '-';

    this.selectedMarkerLayer.set({
      title,
      layout,
      x: $event.originalEvent.clientX + 10,
      y: $event.originalEvent.clientY - 110,
    });
  }

  onMarkerMouseLeave() {
    this.map.getCanvas().style.cursor = '';

    this.selectedMarkerLayer.set(null);
  }
}
